import React from "react";

interface ScheduleConflictModalProps {
  isOpen: boolean;
  conflicts: {
    screenName: string;
    location: string;
    status: string;
    schedules: {
      timeRange: string;
      playlistName: string;
      adGroup: string;
      dateRange?: string;
      daysText?: string;
      conflictMessage?: string;
    }[];
  }[];
  newSchedule?: {
    dateRange: string;
    timeRange: string;
    daysText: string;
    adGroup: string;
    playlistCount: number;
  };
  onClose: () => void;
  onConfirm: () => void;
}

const ScheduleConflictModal: React.FC<ScheduleConflictModalProps> = ({
  isOpen,
  conflicts,
  newSchedule,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="bg-white rounded-lg shadow-lg w-3/5 max-h-[80vh] overflow-y-auto p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Schedule Conflicts Detected
        </h2>
        <p className="text-gray-600 mb-6">
          The following screens already have scheduled playlists during the
          selected time. The new schedule will take priority over existing ones
          during the overlapping period. Existing schedules will resume
          automatically when this schedule ends or is deleted.
        </p>

        {/* New Schedule Section */}
        {newSchedule && (
          <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-3">
              New Schedule
            </h3>
            <div className="bg-white rounded border p-3 shadow-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Date Range</p>
                  <p className="text-sm font-medium text-gray-700">
                    {newSchedule.dateRange}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Time Range</p>
                  <p className="text-sm font-medium text-gray-700">
                    {newSchedule.timeRange}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Days</p>
                  <p className="text-sm font-medium text-gray-700">
                    {newSchedule.daysText}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Playlists</p>
                  <p className="text-sm font-medium text-gray-700">
                    {newSchedule.playlistCount} playlist(s)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Existing Schedules Section */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Existing Conflicting Schedules
          </h3>
        </div>

        {conflicts.map((conflict, idx) => (
          <div
            key={idx}
            className="border rounded-lg p-4 mb-4 shadow-sm bg-gray-50"
          >
            {/* Screen Details */}
            <div className="flex justify-between items-center mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">
                  {conflict.screenName}
                </h3>
                <p className="text-sm text-gray-500">{conflict.location}</p>
              </div>
              <span
                className={`px-3 py-1 text-xs rounded ${
                  conflict.status === "ON"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {conflict.status}
              </span>
            </div>

            {/* Existing Schedules */}
            <div className="space-y-3">
              {conflict.schedules.map((schedule, sIdx) => (
                <div
                  key={sIdx}
                  className="bg-white rounded border-2 border-amber-200 p-3 shadow-sm"
                >
                  <div className="mb-2">
                    <span className="text-xs font-semibold text-amber-600 uppercase">
                      Existing Schedule (will be paused during overlap)
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Date Range</p>
                      <p className="text-sm font-medium text-gray-700">
                        {schedule.dateRange || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Time Range</p>
                      <p className="text-sm font-medium text-gray-700">
                        {schedule.timeRange}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Days</p>
                      <p className="text-sm font-medium text-gray-700">
                        {schedule.daysText || "All Days"}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Playlist</p>
                      <p className="text-sm font-medium text-gray-700">
                        {schedule.playlistName}
                      </p>
                    </div>
                  </div>
                  {schedule.conflictMessage && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-xs text-amber-700">
                        {schedule.conflictMessage}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-primary-default text-white rounded hover:bg-primary-dark"
          >
            Save Anyway
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleConflictModal;
